import { useEffect, useRef, useState } from "react";
import { Image as ImageIcon, Save, Upload, Crop, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useLanguage } from "@/i18n/LanguageContext";
import { HeroCropModal } from "@/components/admin/HeroCropModal";

// Bannière d'accueil : téléversement optimisé automatiquement (WebP, max 2560 px) via /admin/hero/upload,
// avec recadrage optionnel desktop/mobile avant enregistrement.
export const AdminHero = () => {
  const { t } = useLanguage();
  const [current, setCurrent] = useState(null); // {url, url_mobile} en production
  const [file, setFile] = useState(null); // fichier original choisi
  const [originalPreview, setOriginalPreview] = useState(null); // URL locale du fichier original (source du recadrage)
  const [preview, setPreview] = useState(null); // URL affichée dans l'aperçu (recadrée si recadrage appliqué)
  const [cropped, setCropped] = useState(null); // {url, url_mobile} recadrés, déjà téléversés + optimisés
  const [cropOpen, setCropOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const fileInput = useRef(null);

  const load = () =>
    api
      .get("/settings/hero-image")
      .then((r) => setCurrent(r.data?.url ? r.data : null))
      .catch(() => {});

  useEffect(() => {
    load();
  }, []);

  const pickFile = (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (!["image/jpeg", "image/png", "image/webp"].includes(f.type)) {
      toast.error(t("Formats acceptés : JPG, JPEG, PNG, WebP"));
      return;
    }
    if (f.size > 15 * 1024 * 1024) {
      toast.error(t("Image trop lourde (max 15 Mo)"));
      return;
    }
    const localUrl = URL.createObjectURL(f);
    setFile(f);
    setOriginalPreview(localUrl);
    setPreview(localUrl);
    setCropped(null);
  };

  const cancelSelection = () => {
    setFile(null);
    setPreview(null);
    setCropped(null);
    setOriginalPreview(null);
  };

  const uploadBlob = async (blob, filename) => {
    const fd = new FormData();
    fd.append("file", blob, filename);
    const { data } = await api.post("/admin/hero/upload", fd);
    return data.url;
  };

  // Recadrage appliqué : les deux versions sont téléversées + optimisées, l'aperçu passe au cadrage desktop
  const applyCrop = async ({ desktopBlob, mobileBlob }) => {
    setBusy(true);
    try {
      const [url, url_mobile] = await Promise.all([
        uploadBlob(desktopBlob, "banner-desktop.png"),
        uploadBlob(mobileBlob, "banner-mobile.png"),
      ]);
      setCropped({ url, url_mobile });
      setPreview(url);
      setCropOpen(false);
      toast.success(t("Recadrage appliqué — pense à enregistrer."));
    } catch (e) {
      toast.error(t(formatApiError(e.response?.data?.detail)));
    } finally {
      setBusy(false);
    }
  };

  const save = async () => {
    if (!file) return;
    setBusy(true);
    try {
      let urls = cropped;
      if (!urls) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/admin/hero/upload", fd);
        urls = { url: data.url };
      }
      await api.put("/admin/hero-image", urls);
      toast.success(t("Bannière mise à jour — visible sur l'accueil."));
      cancelSelection();
      load();
    } catch (e) {
      toast.error(t(formatApiError(e.response?.data?.detail)));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="admin-hero">
      <h3 className="flex items-center gap-2 text-sm font-bold text-slate-900">
        <ImageIcon className="h-4 w-4 text-[#2563EB]" />
        {t("Image principale du hero")}
      </h3>
      <p className="mt-1 text-xs text-slate-500">
        {t("Cette image s'affiche en grand sur la page d'accueil. Choisis une photo lumineuse et de belle qualité.")}
      </p>

      {current && (
        <div className="mt-4">
          <p className="mb-1.5 text-[11px] font-bold uppercase tracking-wider text-slate-400">{t("Bannière actuelle")}</p>
          <img src={current.url} alt={t("Bannière actuelle")} className="h-36 w-full rounded-2xl border border-slate-200 object-cover sm:h-44" />
        </div>
      )}

      {preview ? (
        <div className="mt-4">
          <p className="mb-1.5 text-[11px] font-bold uppercase tracking-wider text-slate-400">{t("Aperçu")}</p>
          <div className="flex flex-wrap items-start gap-3">
            <img src={preview} alt={t("Aperçu de la nouvelle bannière")} data-testid="hero-preview" className="h-36 flex-1 rounded-2xl border-2 border-[#2563EB] object-cover sm:h-44" />
            {cropped?.url_mobile && (
              <div>
                <img src={cropped.url_mobile} alt={t("Aperçu mobile")} data-testid="hero-preview-mobile" className="aspect-[13/14] h-36 rounded-2xl border border-slate-200 object-cover sm:h-44" />
                <p className="mt-1 text-center text-[10px] font-semibold text-slate-400">{t("Aperçu mobile")}</p>
              </div>
            )}
          </div>
          <div className="mt-3 flex flex-wrap gap-2.5">
            <button
              type="button"
              data-testid="hero-crop-button"
              onClick={() => setCropOpen(true)}
              disabled={busy}
              className="flex items-center gap-2 rounded-full border border-slate-200 px-5 py-2.5 text-sm font-semibold text-slate-700 transition hover:border-blue-300 hover:text-[#2563EB] disabled:opacity-60"
            >
              <Crop className="h-4 w-4" />
              {t("Recadrer l'image")}
            </button>
            <button
              type="button"
              data-testid="hero-save-button"
              onClick={save}
              disabled={busy}
              className="flex items-center gap-2 rounded-full bg-[#2563EB] px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-[#1D4ED8] disabled:opacity-60"
            >
              <Save className="h-4 w-4" />
              {busy ? t("Publication…") : t("Enregistrer")}
            </button>
            <button
              type="button"
              data-testid="hero-cancel-button"
              onClick={cancelSelection}
              className="flex items-center gap-2 rounded-full border border-slate-200 px-5 py-2.5 text-sm font-semibold text-slate-600 transition hover:border-blue-300 hover:text-[#2563EB]"
            >
              <X className="h-4 w-4" />
              {t("Annuler")}
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          data-testid="hero-change-button"
          onClick={() => fileInput.current?.click()}
          className="mt-4 flex w-full items-center justify-center gap-2 rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-4 py-8 text-sm font-semibold text-slate-500 transition hover:border-blue-300 hover:text-[#2563EB]"
        >
          <Upload className="h-4 w-4" />
          {t("Changer l'image")}
        </button>
      )}

      <input
        ref={fileInput}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        data-testid="hero-file-input"
        className="hidden"
        onChange={pickFile}
      />
      <p className="mt-3 text-[11px] leading-relaxed text-slate-400">
        {t("Formats acceptés : JPG, JPEG, PNG, WebP")} • {t("Poids max : 15 Mo")} • {t("Optimisée automatiquement en WebP à l'enregistrement")}
      </p>

      {cropOpen && originalPreview && (
        <HeroCropModal imageSrc={originalPreview} busy={busy} onCancel={() => setCropOpen(false)} onApply={applyCrop} />
      )}
    </div>
  );
};
